import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { Transition } from 'react-transition-group';

import Portal from '#@/plumbing/Portal';
import { lazyLoad } from '#@/shared/utils/lazyLoad';

import { Toolbar } from './components/Toolbar';
import { TOOLBAR_HEIGHT, TRANSITION_DURATION } from './constants';
import { LightboxControlsContext } from './controls/Context';
import useLightboxControls from './controls/useLightboxControls';
import { LightboxContext } from './LightboxContext';
import { Container, Content, LoadingIndicator } from './Styles';

const Document = lazyLoad(() => import('./components/Document').then((m) => m.Document));
const Gallery = lazyLoad(() => import('./components/Gallery').then((m) => m.Gallery));
const Image = lazyLoad(() => import('./components/Image').then((m) => m.Image));

export const Lightbox: React.FC = () => {
  const { file, images, isLoading } = useContext(LightboxContext);
  const controls = useLightboxControls();
  const { setContentContainerSize } = controls;

  const contentContainerRef = useRef<HTMLDivElement | null>(null);

  const updateContentContainerSize = useCallback(() => {
    if (!contentContainerRef.current || isLoading) return;

    const margin = controls.contentMargin * 2;
    const width = contentContainerRef.current.clientWidth - margin;
    const height = contentContainerRef.current.clientHeight - margin - TOOLBAR_HEIGHT;

    setContentContainerSize({ width, height });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls.contentMargin, isLoading, setContentContainerSize, contentContainerRef.current]);

  useEffect(() => {
    updateContentContainerSize();
  }, [updateContentContainerSize]);

  // Update container size state when window size changes
  useEffect(() => {
    window.addEventListener('resize', updateContentContainerSize);

    return () => window.removeEventListener('resize', updateContentContainerSize);
  }, [updateContentContainerSize]);

  const renderContent = () => {
    if (file) {
      return file.type === 'document' ? <Document /> : <Image />;
    }

    if (images) {
      return <Gallery />;
    }

    return null;
  };

  return (
    <Portal>
      <Transition
        in={Boolean(file) || Boolean(images)}
        timeout={TRANSITION_DURATION}
        appear
        unmountOnExit
      >
        {(state) => (
          <LightboxControlsContext.Provider value={controls}>
            <Container $transitionState={state} data-cy-lightbox>
              <Toolbar transitionState={state} />
              {isLoading && <LoadingIndicator $transitionState={state} />}
              <Content $isLoading={isLoading} ref={contentContainerRef}>
                {renderContent()}
              </Content>
            </Container>
          </LightboxControlsContext.Provider>
        )}
      </Transition>
    </Portal>
  );
};
